// Ports of the Android overview's RecordingElapsedTimeline and TrackStretches, so the web reads the
// same moving time, pace and cadence off a recording as the app does.

/** Recordings uploaded without per-point times carry 0, which reads as 1970. */
export const EARLIEST_PLAUSIBLE_TIMESTAMP_MS = 946_684_800_000;

/** Points arrive 5-8s apart, so a longer gap is where a pause could be. */
export const PAUSE_GAP_THRESHOLD_MS = 90_000;

export const STRETCH_TARGET_COUNT = 150;
export const STRETCH_MINIMUM_METERS = 50;

/** A segment ending right on a boundary leaves a rounding sliver in the next stretch. */
const SLIVER_METERS = 1e-6;

export function interpolate(from: number, to: number, fraction: number): number {
  return from + (to - from) * fraction;
}

/** `values` runs alongside the ascending `xs`; past either end, the end value. */
export function valueAt(target: number, xs: number[], values: number[]): number {
  const after = xs.findIndex((x) => x >= target);
  if (after === -1) return values[values.length - 1];
  if (after === 0) return values[0];

  const before = after - 1;
  const span = xs[after] - xs[before];
  return interpolate(values[before], values[after], span > 0 ? (target - xs[before]) / span : 0);
}

/**
 * Moving milliseconds since the first point, one per point, or null without usable times.
 *
 * `movingSeconds` is the recording's own duration, which already leaves out paused time. The
 * difference from the wall clock is taken off the largest gaps, so a rest taken without pausing
 * still counts as trail time. Deltas across a continued recording's leg boundary count for nothing.
 */
export function movingElapsedMs(
  timeStamps: number[] | null | undefined,
  pointCount: number,
  movingSeconds: number | null | undefined,
  segmentStartIndices: number[] = []
): number[] | null {
  if (!timeStamps || pointCount < 2 || timeStamps.length !== pointCount) return null;
  if (timeStamps.some((t) => !Number.isFinite(t) || t < EARLIEST_PLAUSIBLE_TIMESTAMP_MS)) return null;

  const boundaries = new Set(segmentStartIndices);
  const deltas = timeStamps.map((t, i) =>
    i === 0 || boundaries.has(i) ? 0 : Math.max(t - timeStamps[i - 1], 0)
  );
  removePausedTime(deltas, pauseBudgetMs(sum(deltas), movingSeconds));

  let running = 0;
  const elapsed = deltas.map((delta) => (running += delta));
  return running > 0 ? elapsed : null;
}

function pauseBudgetMs(wallClockMs: number, movingSeconds: number | null | undefined): number {
  const movingMs = (movingSeconds ?? 0) * 1000;
  if (movingMs <= 0 || movingMs >= wallClockMs) return 0;
  return wallClockMs - movingMs;
}

function removePausedTime(deltas: number[], budgetMs: number): void {
  if (budgetMs <= 0) return;

  const gapsLargestFirst = deltas
    .map((_, i) => i)
    .filter((i) => deltas[i] > PAUSE_GAP_THRESHOLD_MS)
    .sort((a, b) => deltas[b] - deltas[a] || a - b);

  let remaining = budgetMs;
  for (const index of gapsLargestFirst) {
    if (remaining <= 0) return;
    const removed = Math.min(deltas[index], remaining);
    deltas[index] -= removed;
    remaining -= removed;
  }
}

export interface Stretch {
  meters: number;
  seconds: number;
  /** Anything else spread along the track with the time, such as steps. */
  amount: number;
}

export function stretchCount(totalMeters: number): number {
  return Math.min(Math.max(Math.floor(totalMeters / STRETCH_MINIMUM_METERS), 1), STRETCH_TARGET_COUNT);
}

/** A stretch that is mostly a jump between continued legs has too little ground to measure. */
export function isMeasured(stretch: Stretch, length: number): boolean {
  return stretch.meters >= length * 0.2;
}

/**
 * The track cut into `count` equal stretches of `length` metres, with the moving time spent on
 * each. `amounts` runs alongside `distances`: each entry accrued over the segment ending there.
 */
export function movingStretches(
  distances: number[],
  elapsedSeconds: number[],
  count: number,
  length: number,
  amounts?: number[]
): Stretch[] {
  const stretches: Stretch[] = Array.from({ length: count }, () => ({ meters: 0, seconds: 0, amount: 0 }));
  const stretchAt = (distance: number) => Math.min(Math.max(Math.floor(distance / length), 0), count - 1);

  for (let index = 1; index < distances.length; index++) {
    const time = elapsedSeconds[index] - elapsedSeconds[index - 1];
    if (time <= 0) continue;
    const amount = amounts?.[index] ?? 0;

    const start = distances[index - 1];
    const end = distances[index];
    if (end <= start) {
      stretches[stretchAt(start)].seconds += time;
      stretches[stretchAt(start)].amount += amount;
      continue;
    }

    const first = stretchAt(start);
    const last = stretchAt(end);
    for (let current = first; current <= last; current++) {
      const low = Math.max(start, current * length);
      const high = current === last ? end : Math.min(end, (current + 1) * length);
      if (high - low <= SLIVER_METERS) continue;
      const share = (high - low) / (end - start);
      stretches[current].meters += high - low;
      stretches[current].seconds += time * share;
      stretches[current].amount += amount * share;
    }
  }
  return stretches;
}

export function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}
